import globalWindow from '@shared/core/globals';
import { getConfig } from '@config';
import { addError } from '@shared/utils/logger';

type LoadState = 'idle' | 'loading' | 'loaded' | 'error';
type Subscriber = (googleMaps: typeof google.maps | null) => void;

// Constants
const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-api-script';
const MAX_POLL_ATTEMPTS = 100; // 100 attempts * 50ms = 5 seconds max
const POLL_INTERVAL_MS = 50;

// Module-level singleton state
let state: LoadState = 'idle';
let googleMaps: typeof google.maps | null = null;
let error: Error | null = null;
const subscribers = new Set<Subscriber>();
let pollTimeoutId: ReturnType<typeof setTimeout> | null = null;
let pollAttempts = 0;

/**
 * Check if Google Maps is already in window
 * Note: window.google is typed as `never` to prevent direct access,
 * so we use type assertion here since we're the loader
 */
const extractFromWindow = (): typeof google.maps | null => {
  // Type assertion needed because window.google is intentionally typed as `never`
  return (globalWindow as unknown as { google?: typeof google }).google?.maps || null;
};

/**
 * Check if script tag already exists (O(1) lookup by ID)
 */
const isScriptTagPresent = (): boolean => {
  return document.getElementById(GOOGLE_MAPS_SCRIPT_ID) !== null;
};

/**
 * Notify all subscribers of state change
 */
const notifySubscribers = (): void => {
  subscribers.forEach(callback => callback(googleMaps));
};

/**
 * Clear any active polling timeout
 */
const clearPolling = (): void => {
  if (pollTimeoutId !== null) {
    clearTimeout(pollTimeoutId);
    pollTimeoutId = null;
  }
  pollAttempts = 0;
};

/**
 * Poll for google.maps to be available
 */
const checkGoogleLoaded = (): void => {
  const maps = extractFromWindow();

  if (maps) {
    googleMaps = maps;
    state = 'loaded';
    clearPolling();
    notifySubscribers();
    return;
  }

  pollAttempts++;

  // Ensure we don't poll forever
  if (pollAttempts >= MAX_POLL_ATTEMPTS) {
    state = 'error';
    error = new Error(`Failed to load Google Maps API: window.google.maps not available after ${MAX_POLL_ATTEMPTS * POLL_INTERVAL_MS}ms`);
    console.error(error.message);
    addError(error, {
      context: 'GoogleMapsLoader',
      reason: 'timeout',
      maxAttempts: MAX_POLL_ATTEMPTS,
      pollIntervalMs: POLL_INTERVAL_MS,
      totalWaitMs: MAX_POLL_ATTEMPTS * POLL_INTERVAL_MS,
    });
    clearPolling();
    notifySubscribers();
    return;
  }

  pollTimeoutId = setTimeout(checkGoogleLoaded, POLL_INTERVAL_MS);
};

/**
 * Inject script tag and start loading
 */
const injectScript = (): void => {
  // Check if script already exists (edge case: eager load was present)
  if (isScriptTagPresent()) {
    checkGoogleLoaded();
    return;
  }

  const { googleMapsApiKey } = getConfig();
  const script = document.createElement('script');
  script.id = GOOGLE_MAPS_SCRIPT_ID;

  const url = new URL('https://maps.googleapis.com/maps/api/js');
  url.searchParams.set('key', googleMapsApiKey);
  url.searchParams.set('libraries', 'places,geometry');

  script.src = url.toString();
  script.async = true;

  script.onerror = (event, source, lineno, colno, errorObj) => {
    state = 'error';
    // Use the provided Error object if available, otherwise create one from the message
    if (errorObj) {
      error = errorObj;
    } else {
      const errorMessage = typeof event === 'string' ? event : 'Failed to load Google Maps API script';
      error = new Error(errorMessage);
    }
    console.error(error.message);
    addError(error, {
      context: 'GoogleMapsLoader',
      source: 'injectScript',
      scriptSrc: source || script.src,
      lineno,
      colno,
    });
    clearPolling();
    notifySubscribers();
  };

  script.onload = () => {
    checkGoogleLoaded();
  };

  document.head.appendChild(script);
};

/**
 * Trigger Google Maps API load.
 * Safe to call multiple times - only loads once.
 */
export const loadGoogleMaps = (): void => {
  if (state !== 'idle') {
    return; // Already loading or loaded
  }

  state = 'loading';
  notifySubscribers();
  injectScript();
};

/**
 * Subscribe to load state changes.
 * Callback is called immediately with current state
 * and again when state changes.
 *
 * @returns Unsubscribe function
 */
export const onGoogleMapsReady = (callback: Subscriber): (() => void) => {
  subscribers.add(callback);

  // Immediately call with current state
  callback(googleMaps);

  // Return unsubscribe function
  return () => {
    subscribers.delete(callback);
  };
};

/**
 * Get current Google Maps object (null if not loaded)
 */
export const getGoogleMaps = (): typeof google.maps | null => {
  return googleMaps;
};

/**
 * Check if currently loading
 */
export const isGoogleMapsLoading = (): boolean => {
  return state === 'loading';
};

/**
 * Check if successfully loaded
 */
export const isGoogleMapsLoaded = (): boolean => {
  return state === 'loaded';
};

/**
 * Check if load failed
 */
export const hasGoogleMapsError = (): boolean => {
  return state === 'error';
};

/**
 * Get error if load failed
 */
export const getGoogleMapsError = (): Error | null => {
  return error;
};

/**
 * Reset state (for testing only)
 * @internal
 */
export const _resetGoogleMapsLoader = (): void => {
  clearPolling();
  state = 'idle';
  googleMaps = null;
  error = null;
  subscribers.clear();
};

// Initialize from window if already loaded
const existing = extractFromWindow();
if (existing) {
  googleMaps = existing;
  state = 'loaded';
}
